import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Brain } from 'lucide-react';

const PredictiveHeatmap = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [heatmapData, setHeatmapData] = useState({ cells: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('connection_rate');
  const animationDuration = reducedMotion ? 0 : 0.5;

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    fetchHeatmapData();
  }, [filters, metric]);

  const fetchHeatmapData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/predictive-heatmap?metric=${metric}`);
      const result = await response.json();
      setHeatmapData(result);
    } catch (error) {
      console.error('Error fetching predictive heatmap:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCellData = (weekday, hour) => {
    return heatmapData.cells.find(c => c.weekday === weekday && c.hour === hour);
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return { bg: '#7f1d1d', border: '#dc2626' };
      case 'medium': return { bg: '#78350f', border: '#f59e0b' };
      default: return { bg: '#064e3b', border: '#10b981' };
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Brain className="w-5 h-5 text-indigo-400" />
          Predictive Risk Heatmap
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Future risk zones (1-hour ahead) • Color = predicted performance
        </p>
      </CardHeader>
      <CardContent>
        {/* Metric Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'connection_rate', label: 'Connection Rate' },
            { key: 'api_latency', label: 'API Latency' },
            { key: 'asr_wer', label: 'ASR WER' }
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                metric === m.key
                  ? 'bg-gradient-to-r from-indigo-500 to-ink-700 text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Generating predictions...</div>
          </div>
        ) : heatmapData.cells.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No data available for predictions</p>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
          >
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Hour labels */}
                <div className="flex gap-1 mb-1 ml-12">
                  <div className="w-12"></div>
                  {hours.map(h => (
                    <div key={h} className="w-8 h-6 flex items-center justify-center">
                      <span className="text-[10px] text-slate-500">{h}</span>
                    </div>
                  ))}
                </div>

                {/* Grid */}
                {weekdays.map((day, dayIdx) => (
                  <div key={dayIdx} className="flex gap-1 mb-1">
                    <div className="w-12 h-8 flex items-center justify-end pr-2">
                      <span className="text-xs text-slate-400">{day}</span>
                    </div>
                    {hours.map((hour) => {
                      const cell = getCellData(dayIdx, hour);
                      const colors = cell ? getRiskColor(cell.risk_level) : { bg: '#1e293b', border: '#334155' };
                      const size = cell ? Math.max(8, Math.min(32, cell.sample_size / 2)) : 8;
                      
                      return (
                        <motion.div
                          key={hour}
                          className="w-8 h-8 flex items-center justify-center"
                          initial={reducedMotion ? {} : { scale: 0.7, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            duration: animationDuration,
                            delay: (dayIdx * 24 + hour) * 0.002
                          }}
                        >
                          <div
                            className="rounded cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all"
                            style={{
                              width: `${size}px`,
                              height: `${size}px`,
                              backgroundColor: colors.bg,
                              border: `1px solid ${colors.border}`
                            }}
                            title={cell ? `${day} ${hour}:00 - Risk: ${cell.risk_level} (Δ${(cell.delta * 100).toFixed(1)}%)` : 'No data'}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-6 flex items-center justify-center gap-4 text-xs">
              <span className="text-slate-400">Risk Level:</span>
              {[
                { level: 'low', label: 'Low Risk', color: '#10b981' },
                { level: 'medium', label: 'Medium Risk', color: '#f59e0b' },
                { level: 'high', label: 'High Risk', color: '#dc2626' }
              ].map((item) => (
                <div key={item.level} className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: item.color }}></div>
                  <span className="text-slate-400">{item.label}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            {heatmapData.meta && (
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-indigo-400">{heatmapData.meta.cells_count || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Predicted Cells</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-ink-500">1h</p>
                  <p className="text-xs text-slate-400 mt-1">Forecast Horizon</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{heatmapData.meta.sample_size || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default PredictiveHeatmap;
